import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { formatCurrency } from "../utils/formatters";
import {
  MagnifyingGlass,
  Plus,
  PencilSimple,
  Trash,
  ArrowLeft,
  Package,
  ArrowsClockwise,
} from "phosphor-react";
import { Link } from "react-router-dom";
import { Switch } from "../components/ui/switch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CatalogManagement({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    default_unit_price: 0,
    default_is_taxable: false,
  });

  useEffect(() => {
    fetchCatalog();
  }, [searchQuery, includeInactive]);

  const fetchCatalog = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      params.append("include_inactive", includeInactive.toString());
      params.append("limit", "100");
      
      const response = await fetch(`${API}/catalog?${params.toString()}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (error) {
      console.error("Error fetching catalog:", error);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setFormData({
      description: "",
      default_unit_price: 0,
      default_is_taxable: false,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      description: item.description,
      default_unit_price: item.default_unit_price,
      default_is_taxable: item.default_is_taxable,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description || formData.description.trim().length < 3) {
      toast.error("Description must be at least 3 characters");
      return;
    }

    try {
      const url = editingItem
        ? `${API}/catalog/${editingItem.item_id}`
        : `${API}/catalog`;
      const method = editingItem ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingItem ? "Item updated" : "Item created");
        setIsDialogOpen(false);
        fetchCatalog();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save item");
      }
    } catch (error) {
      toast.error("Failed to save item");
    }
  };

  const toggleItemActive = async (item) => {
    try {
      const response = await fetch(`${API}/catalog/${item.item_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !item.is_active }),
      });

      if (response.ok) {
        toast.success(item.is_active ? "Item deactivated" : "Item reactivated");
        fetchCatalog();
      } else {
        toast.error("Failed to update item");
      }
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  return (
    <div className="space-y-6" data-testid="catalog-management-page">
      {/* Header - Show back link only when not embedded */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {!embedded && (
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <ArrowLeft size={20} />
              </Button>
            </Link>
          )}
          <div>
            <h2 className="font-heading text-xl font-semibold uppercase">
              {embedded ? "Line Item Catalog" : "Catalog Maintenance"}
            </h2>
            <p className="text-sm text-zinc-500">Manage your saved estimate line items</p>
          </div>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
          data-testid="add-catalog-item-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Item
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="border-2 border-zinc-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search catalog items..."
                className="pl-10 h-10 border-2"
                data-testid="catalog-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
                id="include-inactive"
              />
              <Label htmlFor="include-inactive" className="text-sm text-zinc-600 cursor-pointer">
                Show inactive
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Catalog Items List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-zinc-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => (
            <Card
              key={item.item_id}
              className={`border-2 ${item.is_active ? "border-zinc-200" : "border-zinc-100 bg-zinc-50 opacity-60"}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{item.description}</h3>
                      {!item.is_active && (
                        <Badge variant="outline" className="bg-zinc-100 text-zinc-500 border-zinc-300">
                          Inactive
                        </Badge>
                      )}
                      {item.default_is_taxable && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                          Taxable
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-zinc-500">
                      <span className="font-medium text-zinc-700">
                        {formatCurrency(item.default_unit_price)}
                      </span>
                      <span>Used {item.usage_count || 0}x</span>
                      {item.last_used_at && (
                        <span>
                          Last: {new Date(item.last_used_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(item)}
                      className="border-2"
                    >
                      <PencilSimple size={16} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleItemActive(item)}
                      className={`border-2 ${item.is_active ? "text-red-600 hover:bg-red-50" : "text-emerald-600 hover:bg-emerald-50"}`}
                    >
                      {item.is_active ? (
                        <>
                          <Trash size={16} className="mr-1" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <ArrowsClockwise size={16} className="mr-1" />
                          Reactivate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <Package size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">No Catalog Items Yet</h3>
            <p className="text-zinc-500 mb-6">
              {searchQuery
                ? "No items match your search"
                : "Your catalog will be built automatically as you create estimates"}
            </p>
            {!searchQuery && (
              <Button
                onClick={openAddDialog}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
              >
                <Plus size={18} className="mr-2" />
                Add First Item
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              {editingItem ? "Edit Catalog Item" : "Add Catalog Item"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form id="catalog-item-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Description *</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Labor - Standard Rate"
                  className="mt-1 h-11 border-2"
                  required
                  data-testid="catalog-item-description"
                />
              </div>
              <div>
                <Label>Default Price</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                  <Input
                    type="number"
                    value={formData.default_unit_price}
                    onChange={(e) => setFormData({ ...formData, default_unit_price: parseFloat(e.target.value) || 0 })}
                    className="h-11 border-2 pl-7"
                    min="0"
                    step="0.01"
                    data-testid="catalog-item-price"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.default_is_taxable}
                  onCheckedChange={(checked) => setFormData({ ...formData, default_is_taxable: checked })}
                  id="default-taxable"
                  data-testid="catalog-item-taxable"
                />
                <Label htmlFor="default-taxable" className="cursor-pointer">
                  Taxable by default
                </Label>
              </div>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              className="flex-1 sm:flex-none border-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="catalog-item-form"
              className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="save-catalog-item-btn"
            >
              {editingItem ? "Save Changes" : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
